export const FIREBASE_RUNTIME_BINDING_SCHEMA =
  "sokogrid.firebase-runtime-binding.v1" as const;

export type FirebaseRuntimeBinding = Readonly<{
  schema: typeof FIREBASE_RUNTIME_BINDING_SCHEMA;
  target: "staging" | "production";
  projectId: string;
  frontendProjectId: string;
  storageBucket: string;
  functionsRegion: "africa-south1";
}>;

const RUNTIME_BINDING_KEYS = [
  "schema",
  "target",
  "projectId",
  "frontendProjectId",
  "storageBucket",
  "functionsRegion",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>) {
  const actual = Object.keys(value).sort();
  const expected = [...RUNTIME_BINDING_KEYS].sort();

  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}

export function parseFirebaseRuntimeBinding(
  value: unknown
): FirebaseRuntimeBinding {
  if (typeof value !== "string" || !value || value.length > 512) {
    throw new Error("Invalid Firebase runtime build binding.");
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error("Invalid Firebase runtime build binding.");
  }

  if (!isRecord(parsed) || !hasExactKeys(parsed)) {
    throw new Error("Invalid Firebase runtime build binding.");
  }

  const target = parsed.target;
  const expectedProjectId =
    target === "staging"
      ? "soko-grid-staging"
      : target === "production"
        ? "soko-grid"
        : "";

  if (
    parsed.schema !== FIREBASE_RUNTIME_BINDING_SCHEMA ||
    !expectedProjectId ||
    parsed.projectId !== expectedProjectId ||
    parsed.frontendProjectId !== expectedProjectId ||
    parsed.storageBucket !== `${expectedProjectId}.firebasestorage.app` ||
    parsed.functionsRegion !== "africa-south1"
  ) {
    throw new Error("Invalid Firebase runtime build binding.");
  }

  return Object.freeze({
    schema: FIREBASE_RUNTIME_BINDING_SCHEMA,
    target,
    projectId: parsed.projectId,
    frontendProjectId: parsed.frontendProjectId,
    storageBucket: parsed.storageBucket,
    functionsRegion: parsed.functionsRegion,
  }) as FirebaseRuntimeBinding;
}

// Expo replaces this direct reference at export time. The result is parsed and
// validated before lib/firebase.ts can initialize any Firebase SDK service.
export const FIREBASE_RUNTIME_BINDING = parseFirebaseRuntimeBinding(
  process.env.EXPO_PUBLIC_SOKOGRID_FIREBASE_BINDING
);
